<template>
    <div class="container-fluid p-0 main-content-wrapper">
        <b-card no-body class="border-0">

            <!-- TABS WITH LOGO -->
            <b-tabs v-model="tabIndex" card class="modern-tabs">

                <!-- Slot that appears BEFORE the first tab -->
                <template #tabs-start>
                    <div class="tabs-logo d-flex align-items-center">
                        <img :src="logo" alt="App Logo" height="50" />
                    </div>
                </template>

                <!-- Tabs -->
                <b-tab title="Introduction" active>
                    <div class="hero-section">
                        <div class="hero-content text-center">
                            <h1 class="display-3 font-weight-bold">Visualize Climate Change</h1>
                            <p class="lead mb-4">Explore interactive tools to make sense of climate projection data.</p>

                            <div class="stripe-image-container">
                                <img src="@/assets/newplot.png" alt="Climate Stripes" class="img-fluid">
                            </div>

                            <b-button variant="primary" size="lg" class="mt-5" @click="tabIndex = 1">
                                Start Exploring <b-icon icon="arrow-right-circle-fill" class="ml-2"></b-icon>
                            </b-button>
                        </div>
                    </div>

                    <section class="features-section py-5">
                        <div class="container">
                            <div class="row text-center">
                                <div class="col-md-4 mb-4">
                                    <div class="feature-card">
                                        <b-icon icon="graph-up" font-scale="3" class="text-primary mb-3"></b-icon>
                                        <h3 class="h5">Interactive Time-Series</h3>
                                        <p class="text-muted">Visualize climate indicators over time. Select models, scenarios, and temporal aggregations to see detailed projections.</p>
                                    </div>
                                </div>

                                <div class="col-md-4 mb-4">
                                    <div class="feature-card">
                                        <b-icon icon="map-fill" font-scale="3" class="text-primary mb-3"></b-icon>
                                        <h3 class="h5">Geospatial Map Explorer</h3>
                                        <p class="text-muted">Explore climate data spatially. View projections on an interactive map to understand regional impacts and variations.</p>
                                    </div>
                                </div>

                                <div class="col-md-4 mb-4">
                                    <div class="feature-card">
                                        <b-icon icon="tools" font-scale="3" class="text-primary mb-3"></b-icon>
                                        <h3 class="h5">Analysis Tools</h3>
                                        <p class="text-muted">Use tools like Climate Stripes and Ensemble Analysis to compare model agreement and communicate climate change.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </b-tab>

                <b-tab title="Climate Data Visualization">
                    <ClimateExplorerTool />
                </b-tab>

                <b-tab title="Forestry Data Visualization">
                    <ForestryExplorerTool />
                </b-tab>

                <b-tab title="Map Explorer">
                    <SpatialExplorerTool :is-active="tabIndex === 3" />
                </b-tab>

                <b-tab title="Climate Stripes">
                    <ClimateStripesTool />
                </b-tab>

                <b-tab title="Ensemble Analysis">
                    <EnsembleAnalysisTool />
                </b-tab>

                <b-tab title="Impressum">
                    <LegalNotice />
                </b-tab>

            </b-tabs>
        </b-card>
    </div>
</template>

<script>
import ClimateExplorerTool from './ClimateExplorerTool.vue';
import ForestryExplorerTool from './ForestryExplorerTool.vue';
import SpatialExplorerTool from './SpatialExplorerTool.vue';
import EnsembleAnalysisTool from './EnsembleAnalysisTool.vue';
import ClimateStripesTool from './ClimateStripesTool.vue';
import LegalNotice from './LegalNotice.vue';

import logo from '@/assets/logo_tud.png';


export default {
    name: 'MainContent',
    components: {
        ClimateExplorerTool,
        ForestryExplorerTool,
        SpatialExplorerTool,
        EnsembleAnalysisTool,
        ClimateStripesTool,
        LegalNotice
    },
    data() {
        return {
            tabIndex: 0
        };
    },
    computed: {
        logo() { return logo; }
    },
    watch: {
        tabIndex(newVal) {
            // when Map Explorer tab becomes active, notify children to resize
            if (newVal === 3) {
                window.dispatchEvent(new Event('map-resize'));
            }
        }
    }
};
</script>

<style scoped>
/* Sticky tab header (now your main header) */
.modern-tabs >>> .nav-tabs {
    position: sticky;
    top: 0;
    z-index: 100;
    background-color: var(--card-background-color);
    padding-left: 12px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid var(--border-color);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

/* Logo inside tabs bar */
.tabs-logo {
    display: flex;
    align-items: center;
    padding-right: 1rem;
    border-right: 1px solid var(--border-color);
}

.tabs-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--text-color);
}

/* Tab styling */
.modern-tabs >>> .nav-link {
    border: none;
    border-bottom: 3px solid transparent;
    color: var(--text-color-muted);
    font-weight: 500;
    margin-left: 0.5rem;
    transition: 0.3s;
}

.modern-tabs >>> .nav-link.active,
.modern-tabs >>> .nav-link:hover {
    color: var(--link-color);
    border-bottom-color: var(--link-color);
}

/* Keep your old hero + features styles */
.hero-section {
    position: relative;
    padding: 6rem 2rem;
    color: var(--hero-text-color);
    overflow: hidden;
}
.hero-section::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(45deg, #0d324d, #7f5a83, #0d324d, #7f5a83);
    background-size: 400% 400%;
    animation: gradientAnimation 15s ease infinite;
    z-index: 1;
}
.hero-content { position: relative; z-index: 2; }
.stripe-image-container { max-width: 900px; margin: 2.5rem auto 0; overflow: hidden; border-radius: .5rem; }

/* Features section */
.feature-card {
    background: var(--card-background-color);
    padding: 2rem;
    border-radius: .5rem;
    box-shadow: 0 .5rem 1rem var(--card-shadow-color);
    animation: fadeInUp .6s ease-out forwards;
    /*opacity: 0;*/
}

@keyframes gradientAnimation {
    0% {
        background-position: 0% 50%;
    }
    50% {
        background-position: 100% 50%;
    }
    100% {
        background-position: 0% 50%;
    }
}

.hero-content h1,
.hero-content p {
    color: #ffffff !important;
    font-weight: 400 !important; /* ensures bold */
}
</style>
